import { SyntheticEvent, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { InfoOutlined } from "@mui/icons-material";
import { TabContext, TabPanel, tabPanelClasses } from "@mui/lab";
import {
  Box,
  CardContent,
  Tab,
  Tooltip as MuiTooltip,
  Typography as T,
  styled,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import ConceptTitleCard from "catalog/components/ConceptTitleCard";
import {
  SiteDetails,
  DemographicBreakdowns,
  ParticipantBreakdown,
} from "catalog/types/interfaces/conceptResponse";
import { DEFAULT_OPTS, splitAxisLabel } from "catalog/utils/chart";
import { ChartData, ChartOptions } from "chart.js";
import { merge } from "chart.js/helpers";
import ContainedTabs from "~/components/ContainedTabs";
import FlatCard from "~/components/FlatCard";
import ListCard, { ExpandableListItem } from "~/components/ListCard";
import { COLOR_PALETTE } from "~/customer/chartJS/colors";
import theme from "~/theme";

const compactFormatter = new Intl.NumberFormat("en", { notation: "compact" });

// Common config shared between histology total count and breakdown bar charts
const BAR_CHART_OPTS: ChartOptions<"bar"> = {
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: false,
    },
  },
};

const HISTOLOGY_TOTAL_OPTS = merge<ChartOptions<"bar">, ChartOptions<"bar">[]>(
  {
    indexAxis: "y",
    scales: {
      x: {
        title: {
          display: true,
          text: "Participant Count",
        },
        ticks: {
          callback: (value) => {
            if (!isNaN(Number(value))) {
              return compactFormatter.format(Number(value));
            }
            return value;
          },
        },
      },
      y: {
        grid: { display: false },
      },
    },
    plugins: {
      tooltip: {
        xAlign: "left",
        yAlign: "center",
      },
    },
  },
  [BAR_CHART_OPTS, DEFAULT_OPTS]
);

const HISTOLOGY_BREAKDOWN_OPTS = merge<ChartOptions<"bar">, ChartOptions<"bar">[]>(
  {
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        title: {
          display: true,
          text: "Participant Count",
        },
        ticks: {
          callback: (value) => {
            if (!isNaN(Number(value))) {
              return compactFormatter.format(Number(value));
            }
            return value;
          },
        },
      },
    },
  },
  [BAR_CHART_OPTS, DEFAULT_OPTS]
);

const CUMULATIVE_CHART_OPTS: ChartOptions<"line"> = merge<
  ChartOptions<"line">,
  ChartOptions<"line">[]
>(
  {
    scales: {
      x: {
        title: {
          display: true,
          text: "Diagnosis Year",
        },
      },
      y: {
        title: {
          display: true,
          text: "Participant Count",
        },
        grid: { display: false },
        stacked: true,
        ticks: {
          callback: (value) => {
            if (!value) {
              return "";
            }

            if (!isNaN(Number(value))) {
              return compactFormatter.format(Number(value));
            }
            return value;
          },
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rect",
        },
      },
      tooltip: {
        yAlign: "center",
      },
    },
    elements: {
      line: {
        tension: 0.1,
        fill: true,
      },
      point: {
        radius: 0,
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  },
  DEFAULT_OPTS
);

const transformCumulativeData = (
  data: SiteDetails["distinct_counts_over_time"],
  sortedHistologies: SiteDetails["histologies"]
): ChartData<"line"> => {
  const datasets: { [key: string]: number[] } = {};
  const cumulativeCounts: { [histologyName: string]: number } = {};
  const yearlyData: { [year: string]: { [histologyName: string]: number } } = {};

  data.forEach((point) => {
    const year = new Date(point.start_date).getUTCFullYear().toString();
    point.histology_counts.forEach((histology) => {
      const [histologyName, count] = Object.entries(histology)[0];
      const isTopTen =
        sortedHistologies.findIndex(
          ({ histological_group_name }) => histological_group_name === histologyName
        ) < 10;
      // Group conditions after top 10 unique names
      const key = isTopTen ? histologyName : "Other";

      if (!yearlyData[year]) {
        yearlyData[year] = {};
      }
      if (!yearlyData[year][key]) {
        yearlyData[year][key] = 0;
      }
      yearlyData[year][key] += count;

      // Initialize cumulativeCounts for each histology
      if (!cumulativeCounts[key]) {
        cumulativeCounts[key] = 0;
      }
    });
  });

  const labels = Object.keys(yearlyData).sort();

  labels.forEach((year, index) => {
    Object.keys(yearlyData[year]).forEach((histologyName) => {
      if (!datasets[histologyName]) {
        // Initialize dataset entry with zero to span gaps
        datasets[histologyName] = new Array(labels.length).fill(0);
      }
      const count = yearlyData[year][histologyName];
      cumulativeCounts[histologyName] += count;
      datasets[histologyName][index] = cumulativeCounts[histologyName];
    });
  });

  Object.keys(datasets).forEach((histologyName) => {
    let lastKnownCount = 0;
    datasets[histologyName].forEach((count, index) => {
      if (count) {
        lastKnownCount = count;
      } else {
        // Carry forward the last known count to avoid gaps
        datasets[histologyName][index] = lastKnownCount;
      }
    });
  });

  // Map datasets to Chart.js format
  const chartDatasets = Object.keys(datasets)
    .sort((a, b) => cumulativeCounts[b] - cumulativeCounts[a])
    .map((histologyName, index) => {
      const { augmentColor } = theme.palette;
      const borderColor = COLOR_PALETTE[index] ?? grey[300];
      const background = augmentColor({ color: { main: borderColor } }).light;
      return {
        label: histologyName,
        data: datasets[histologyName],
        backgroundColor: background,
        borderColor: borderColor,
      };
    });

  return {
    labels: labels.map((year) => year.toString()),
    datasets: chartDatasets,
  };
};

const HistologyBreakdownChart = (props: { data: DemographicBreakdowns<ParticipantBreakdown> }) => {
  const [chartTab, setChartTab] = useState<"race" | "gender">("gender");

  const getChartData = (data: ParticipantBreakdown[]) => {
    return {
      labels: data.map(({ concept_name }) => splitAxisLabel(concept_name, "or")),
      datasets: [
        {
          label: "Participant Count",
          data: data.map(({ participant_count }) => participant_count),
        },
      ],
    };
  };

  return (
    <TabContext value={chartTab}>
      <Box display="flex" justifyContent="center" gap={2}>
        <ContainedTabs
          value={chartTab}
          onChange={(_: SyntheticEvent, value: any) => setChartTab(value)}
        >
          <Tab label="Gender" value="gender" />
          <Tab label="Race" value="race" />
        </ContainedTabs>
      </Box>
      <TabPanel value="race" className="chart-container">
        <Bar data={getChartData(props.data.race_counts)} options={HISTOLOGY_BREAKDOWN_OPTS} />
      </TabPanel>
      <TabPanel value="gender" className="chart-container">
        <Bar data={getChartData(props.data.gender_counts)} options={HISTOLOGY_BREAKDOWN_OPTS} />
      </TabPanel>
    </TabContext>
  );
};

const formatCountLabel = (participantCount: number, totalCount: number) => {
  const participantPercent = Math.round((participantCount / totalCount) * 100);
  if (!participantCount) {
    return "(none)";
  } else {
    return `${participantCount} (${participantPercent || "<1"}%)`;
  }
};

const InfoIcon = styled(InfoOutlined)({ opacity: 0.5, fontSize: 18 });

const CancerConceptDetail = (props: { data: SiteDetails }) => {
  const [chartTab, setChartTab] = useState("total");
  const sortedHistologies = props.data.histologies?.sort(
    (a, b) => b.participant_count - a.participant_count
  );

  return (
    <>
      <ConceptTitleCard data={props.data} />
      {sortedHistologies && (
        <FlatCard sx={{ [`.${tabPanelClasses.root}`]: { px: 1, py: 3 } }}>
          <CardContent>
            <TabContext value={chartTab}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <T variant="body1" fontWeight={600}>
                  Histology Breakdown
                </T>
                <ContainedTabs
                  value={chartTab}
                  onChange={(_: SyntheticEvent, value: any) => setChartTab(value)}
                >
                  <Tab label="Total" value="total" />
                  <Tab label="Cumulative" value="cumulative" />
                </ContainedTabs>
              </Box>
              <TabPanel value="total" className="chart-container">
                <Bar
                  data={{
                    labels: sortedHistologies.map((item) => item.histological_group_name),
                    datasets: [
                      {
                        label: "Participant Count",
                        data: sortedHistologies.map((item) => item.participant_count),
                      },
                    ],
                  }}
                  options={HISTOLOGY_TOTAL_OPTS}
                />
              </TabPanel>
              <TabPanel value="cumulative" className="chart-container">
                <Line
                  data={transformCumulativeData(
                    props.data.distinct_counts_over_time,
                    sortedHistologies
                  )}
                  options={CUMULATIVE_CHART_OPTS}
                />
              </TabPanel>
            </TabContext>
          </CardContent>
        </FlatCard>
      )}
      {sortedHistologies && (
        <ListCard
          borderTop
          header={
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pt={2}
              px={2}
              gap={1}
            >
              <Box display="flex" alignItems="center" gap={0.5}>
                <T variant="body1" fontWeight={600} whiteSpace="nowrap">
                  Specific Histology
                </T>
                <MuiTooltip
                  arrow
                  title="Breakdown of cancer incidences by three-digit histological group codes."
                >
                  <InfoIcon />
                </MuiTooltip>
              </Box>
              <T variant="body2" sx={{ opacity: 0.5, fontWeight: 500, textAlign: "end", pr: 1 }}>
                Total Participants
              </T>
            </Box>
          }
        >
          {sortedHistologies.map((histology, index) => {
            const conditionBreakdown = props.data.participant_breakdown.find(
              ({ histological_group_name }) =>
                histological_group_name === histology.histological_group_name
            );
            return (
              <ExpandableListItem
                key={histology.histological_group_name}
                expansionContent={
                  // TODO: How to best handle this if participant breakdowns are missing for some reason?
                  conditionBreakdown ? <HistologyBreakdownChart data={conditionBreakdown} /> : null
                }
                rootStyles={{ px: 0.5 }}
              >
                <T variant="body2" fontWeight={500} flex={1} py={0.5}>
                  {index + 1}. {histology.histological_group_name}
                </T>
                <Box display="flex" alignItems="center" gap={0.5} fontSize={18}>
                  <T
                    variant="body2"
                    sx={{ opacity: 0.67, display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    {formatCountLabel(
                      histology.participant_count,
                      props.data.participant_count ?? 0
                    )}
                  </T>
                </Box>
              </ExpandableListItem>
            );
          })}
        </ListCard>
      )}
    </>
  );
};

export default CancerConceptDetail;
